import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./ResponseMarkdown.css";

// Shared markdown renderer for AI model responses. Citation markers like
// "[3]" become hoverable links into `citations[2]` (Perplexity, Gemini and
// Claude responses carry them; OpenAI uses inline markdown links instead).

// "[3]" → "[3](#cite-3)" so react-markdown hands us a link we can decorate.
// A marker already followed by "(" is a real markdown link and is left alone.
const MARKER_RE = /\[(\d{1,3})\](?!\()/g;
const HAS_MARKER_RE = /\[\d{1,3}\](?!\()/;
const linkifyMarkers = (text) => text.replace(MARKER_RE, "[$1](#cite-$1)");

export const domainOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url || ""; }
};

// Which [n] markers actually appear in the text (Perplexity returns more
// sources than it cites, so a 30-item list with markers up to [5] is normal).
export const citedIndices = (text) => {
  const out = new Set();
  for (const m of (text || "").matchAll(/\[(\d{1,3})\](?!\()/g)) out.add(Number(m[1]));
  return out;
};

function CiteMarker({ n, citation }) {
  if (!citation) {
    // Runs stored before ordered citations existed: the sources are listed
    // below, but which one this marker points at was never saved.
    return (
      <sup className="cite cite-unknown">
        <span>[{n}]</span>
        <span className="cite-card" role="tooltip">
          <span className="cite-title">Source order not recorded</span>
          <span>This run predates ordered citation tracking, so [{n}] can&apos;t be
            matched to a link. All sources are listed under the response.</span>
        </span>
      </sup>
    );
  }
  const domain = citation.domain || domainOf(citation.url);
  return (
    <sup className="cite">
      <a href={citation.url} target="_blank" rel="noopener noreferrer">[{n}]</a>
      <span className="cite-card" role="tooltip">
        {citation.title && <span className="cite-title">{citation.title}</span>}
        <span className="cite-domain">{domain}</span>
        <span className="cite-url">{citation.url}</span>
      </span>
    </sup>
  );
}

export default function ResponseMarkdown({ text, className = "", citations = [] }) {
  if (!text) return null;
  const source = HAS_MARKER_RE.test(text) ? linkifyMarkers(text) : text;
  return (
    <div className={`response-md ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, children, href, ...props }) => {
            const m = /^#cite-(\d+)$/.exec(href || "");
            if (m) {
              const n = Number(m[1]);
              return <CiteMarker n={n} citation={citations?.[n - 1]} />;
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            );
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}

// Collapsible numbered source list under a response. With ordered citations,
// entry n is what [n] points at and uncited entries are dimmed. Older runs
// only have an unordered URL set, and the summary says so.
export function SourcesList({ citations = [], urls = [], text = "" }) {
  const ordered = citations?.length > 0;
  const items = ordered ? citations : (urls || []).map((u) => ({ url: u, domain: domainOf(u) }));
  if (!items.length) return null;
  const cited = citedIndices(text);
  let summary = `Sources (${items.length})`;
  if (!ordered) summary += " · order not recorded for this run";
  else if (cited.size > 0 && cited.size < items.length) summary += ` · ${cited.size} cited in the text`;
  return (
    <details className="response-sources">
      <summary>{summary}</summary>
      <ol>
        {items.map((c, i) => (
          <li key={`${c.url}-${i}`} className={ordered && cited.size > 0 && !cited.has(i + 1) ? "uncited" : ""}>
            <a href={c.url} target="_blank" rel="noopener noreferrer">{c.domain || domainOf(c.url)}</a>
            {c.title && <span className="muted"> — {c.title}</span>}
          </li>
        ))}
      </ol>
    </details>
  );
}
